import type { MultiSelectOption } from "../../MultiSelect";
import type { MarketingBannerAsset } from "../MarketingBannerConfigurationModal/types";
import type { MarketingBannerRow } from "../types";

export type MarketingMaterial = {
  id: string;
  banner: MarketingBannerRow;
  title: string;
  languages: string[];
  sizes: string[];
  tags: string[];
  offerId: number;
  assets: MarketingBannerAsset[];
};

export type LabelOption = { value: string; label: string };

export type BannerLibraryPage = {
  items: MarketingMaterial[];
  nextPage: number | null;
  // Page-derived filter options. Optional: apps that supply a dedicated
  // `loadFilterOptions` source these from an endpoint instead and may omit them.
  availableLanguageOptions?: LabelOption[];
  availableSizeOptions?: LabelOption[];
};

/**
 * Optional injected loader for the complete filter dropdown values, fetched once
 * (independent of pagination and active filters). When provided, it is the
 * source of truth for the Language/Size options and the page-derived options are
 * ignored. MUST be referentially stable (wrap in useCallback/useRef).
 */
export type LoadBannerFilterOptions = () => Promise<{
  languageOptions: LabelOption[];
  sizeOptions: LabelOption[];
}>;

export type BannerLibraryFilterType = "all" | "html" | "image";

export type LoadBannerPageParams = {
  type: BannerLibraryFilterType;
  languages: string[];
  sizes: string[];
  page: number;
};

/**
 * Injected data loader — each app maps these params to its own API call
 * (portal: React Query fetcher; backoffice: server action) and returns a
 * normalised page. MUST be referentially stable (wrap in useCallback/useRef).
 */
export type LoadBannerPage = (
  params: LoadBannerPageParams,
) => Promise<BannerLibraryPage>;

export type BannerLibraryFilterDraft = {
  language: MultiSelectOption[];
  bannerSize: MultiSelectOption[];
};

export const EMPTY_BANNER_LIBRARY_FILTER: BannerLibraryFilterDraft = {
  language: [],
  bannerSize: [],
};

export type BannerLibraryTranslations = {
  backButton: string;
  title: string;
  filterButton: string;
  filterAriaLabel: string;
  filters: { all: string; html: string; image: string };
  error: { title: string; text: string; action: string };
  filterModal: {
    title: string;
    languageLabel: string;
    languagePlaceholder: string;
    bannerSizeLabel: string;
    bannerSizePlaceholder: string;
    clear: string;
    apply: string;
  };
  empty: { title: string; subtitle: string; resetButton: string };
};
